import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore'
import { db, APP_COLLECTION, APP_DOC } from '../lib/firebase'
import { useAuth } from './AuthContext'
import { DEFAULT_DEGREES, DEFAULT_DEPARTMENTS } from '../data/degrees'
import { DEFAULT_COURSES } from '../data/courses'
import { hydrateFromFirestore } from '../data/moduleSchema'

/**
 * Degrees, departments, courses, and published custom modules loaded from
 * Firestore so an admin can create, configure, and assign them without a
 * deploy.
 */

const ConfigContext = createContext(null)

const CONFIG = { degrees: 'degrees', departments: 'departments', courses: 'courses' }

export function ConfigProvider({ children }) {
  const { user } = useAuth()
  const [degrees, setDegrees] = useState(DEFAULT_DEGREES)
  const [departments, setDepartments] = useState(DEFAULT_DEPARTMENTS)
  const [courses, setCourses] = useState(DEFAULT_COURSES)
  const [customModules, setCustomModules] = useState([])
  const [loading, setLoading] = useState(true)
  const [seeded, setSeeded] = useState({ degrees: false, departments: false, courses: false })

  const loadCustomModules = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, APP_COLLECTION, APP_DOC, 'custom_modules'))
      // Doc id last: it wins over the `id` the document also stores, which
      // is what the URL and the progress doc are keyed on. Same order in the
      // eager load below, in ModuleRouter, and in aiStudioService — a module
      // that answers to two different ids depending on who loaded it is the
      // drift this list exists to prevent.
      const list = snap.docs.map((d) => hydrateFromFirestore({ ...d.data(), id: d.id }))
      setCustomModules(list)
      return list
    } catch (err) {
      console.warn('[config] could not load custom_modules:', err)
      return []
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    async function load() {
      if (!user?.uid) {
        setLoading(false)
        return
      }
      try {
        const [degreeSnap, deptSnap, courseSnap, customSnap] = await Promise.all([
          getDoc(doc(db, APP_COLLECTION, APP_DOC, 'config', CONFIG.degrees)),
          getDoc(doc(db, APP_COLLECTION, APP_DOC, 'config', CONFIG.departments)),
          getDoc(doc(db, APP_COLLECTION, APP_DOC, 'config', CONFIG.courses)),
          getDocs(collection(db, APP_COLLECTION, APP_DOC, 'custom_modules')).catch(() => ({ docs: [] })),
        ])
        if (cancelled) return

        const savedDegrees = degreeSnap.exists() ? degreeSnap.data().degrees : null
        const savedDepartments = deptSnap.exists() ? deptSnap.data().departments : null
        const savedCourses = courseSnap.exists() ? courseSnap.data().courses : null
        const loadedCustom = customSnap?.docs
          ? customSnap.docs.map((d) => hydrateFromFirestore({ ...d.data(), id: d.id }))
          : []

        setDegrees(Array.isArray(savedDegrees) ? savedDegrees : DEFAULT_DEGREES)
        setDepartments(Array.isArray(savedDepartments) ? savedDepartments : DEFAULT_DEPARTMENTS)
        // No `.length` guard, deliberately: an admin who deletes every course
        // has saved an empty list, and treating that as "nothing saved" would
        // resurrect the four seed courses on the next load as if the deletion
        // never happened. Same shape as degrees and departments above.
        setCourses(Array.isArray(savedCourses) ? savedCourses : DEFAULT_COURSES)
        setCustomModules(loadedCustom)
        setSeeded({
          degrees: Array.isArray(savedDegrees),
          departments: Array.isArray(savedDepartments),
          courses: Array.isArray(savedCourses),
        })
      } catch (err) {
        console.error('[config] could not load degrees/departments/courses:', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [user?.uid])

  const SETTERS = { degrees: setDegrees, departments: setDepartments, courses: setCourses }

  const save = useCallback(async (which, value) => {
    const key = CONFIG[which] ? which : 'departments'
    try {
      await setDoc(doc(db, APP_COLLECTION, APP_DOC, 'config', CONFIG[key]), { [key]: value })
      SETTERS[key](value)
      setSeeded((s) => ({ ...s, [key]: true }))
      return { ok: true }
    } catch (err) {
      console.error(`[config] could not save ${key}:`, err)
      return { ok: false, error: err }
    }
    // SETTERS is rebuilt each render but holds only useState setters, which are
    // stable for the life of the component — so it is deliberately not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Courses nobody created: a module naming a `track` that no course declares.
  // They are surfaced rather than swallowed — see the Courses admin page, which
  // names the module responsible — but they still have to appear in allCourses,
  // because their modules are real and have to render somewhere.
  const customCourses = useMemo(() => {
    const map = new Map()
    for (const m of customModules || []) {
      const courseId = m.track ? String(m.track).trim().toLowerCase() : ''
      if (!courseId || courses.some((t) => t.id === courseId)) continue
      if (!map.has(courseId)) {
        const titleCaseName = courseId.charAt(0).toUpperCase() + courseId.slice(1).replace(/-/g, ' ')
        map.set(courseId, {
          id: courseId,
          name: titleCaseName,
          description: `${titleCaseName} training modules`,
          isCustom: true,
        })
      }
    }
    return Array.from(map.values())
  }, [customModules, courses])

  const allCourses = useMemo(() => [...courses, ...customCourses], [courses, customCourses])

  const value = useMemo(
    () => ({
      degrees,
      departments,
      courses,
      customModules,
      customCourses,
      allCourses,
      refreshCustomModules: loadCustomModules,
      loading,
      // False until an admin has saved: the pages say so, because editing
      // something that is really a default is worth knowing about.
      seeded,
      saveDegrees: (next) => save('degrees', next),
      saveDepartments: (next) => save('departments', next),
      saveCourses: (next) => save('courses', next),
    }),
    [degrees, departments, courses, customModules, customCourses, allCourses, loadCustomModules, loading, seeded, save]
  )

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
}

export function useConfig() {
  const ctx = useContext(ConfigContext)
  if (!ctx) throw new Error('useConfig must be used inside a ConfigProvider')
  return ctx
}
